import React, { useEffect, useRef, useState } from 'react';
import { TreeNode } from '../../../../types/Post';
import {ActionButton} from "../../../../designSystem/components/ActionButton";

interface TreeItemProps {
  node: TreeNode;
  depth?: number;
  selectedId?: string | null;
  editMode: boolean;
  onSelect?: (n: TreeNode) => void;
  onMove?: (n: TreeNode) => void;
  onChangeCover?: (n: TreeNode) => void;
  onAdd?: (parent: TreeNode) => void;
  onRename?: (n: TreeNode) => void;
  onDelete?: (n: TreeNode) => void;
  isNodeSelectable?: boolean;
  isLeafSelectable?: boolean;
}

export const TreeItem = ({
  node,
  depth = 0,
  selectedId,
  onSelect,
  onAdd,
  onMove,
  onChangeCover,
  onRename,
  onDelete,
  editMode,
  isNodeSelectable = true,
  isLeafSelectable = true,
}: TreeItemProps) => {
  const [open, setOpen] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const hasKids = node.children.length > 0;
  const selected = selectedId === node.id;
  const menuRef = useRef<HTMLDivElement | null>(null);
  const moreBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(t) &&
        moreBtnRef.current &&
        !moreBtnRef.current.contains(t)
      ) {
        setMenuOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
    };
  }, [menuOpen]);

  const selectable = (
    (isNodeSelectable && ([0, 1, 2].includes(node.level))) ||
    (isLeafSelectable && (node.level === 3))
  )

  return (
    <div className={depth === 0 ? '' : 'ml-3'}>
      <div
        className={`
          group flex items-center justify-between rounded-md my-1 px-2 py-1 transition-colors
          ${(selected) ? 'bg-gray-100 hover:bg-gray-100' : ''}
          ${(selectable) ? 'hover:bg-gray-100' : ''}
        `}
      >
        <div
          className="flex items-center gap-1 cursor-pointer flex-grow"
          onClick={(e) => {
            if (selectable) {
              e.stopPropagation();
              onSelect?.(node);
            }
          }}
        >
          {hasKids ? (
            <span
              className="w-4 text-center select-none"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(!open);
              }}
              title={open ? '收合' : '展開'}
            >
              {open ? <i className="fa fa-chevron-down"/> : <i className="fa fa-chevron-right"/>}
            </span>
          ) : (
            <span className="w-4"/>
          )}

          <span
            className="px-1.5 py-0.5 inline-flex items-center whitespace-nowrap overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300"
            title={`level ${node.level}`}
          >
            {node.level === 3 && <i className="fa fa-folder mr-2 text-gray-600"/>}
            <span className="inline-block">{node.name}</span>
          </span>
        </div>

        {editMode ? (
          <div
            className="flex items-center gap-1 ml-2 relative opacity-0 group-hover:opacity-100 transition-opacity duration-150"
          >
            {((node.level === 0 && node.children.length < 10) || (node.level >= 1 && node.level < 3)) && (
              <ActionButton
                iconName="fa-plus"
                variant="ghost"
                size="sm"
                onClick={() => onAdd?.(node)}
              />
            )}

            {node.level > 0 && (
              <ActionButton
                ref={moreBtnRef}
                iconName="fa-ellipsis"
                variant="ghost"
                size="sm"
                onClick={() => setMenuOpen((v) => !v)}
              />
            )}

            {/* Dropdown Menu */}
            {(node.level > 0 && menuOpen) && (
              <div
                ref={menuRef}
                className="absolute right-0 top-[calc(100%+4px)] z-20 min-w-[140px] rounded-md border border-gray-200 bg-white shadow-lg py-1"
              >
                {node.level === 1 && (
                  <ActionButton
                    fullWidth
                    iconName="fa-image"
                    label="封面圖片"
                    variant="ghost"
                    onClick={() => {
                      setMenuOpen(false);
                      onChangeCover?.(node);
                    }}
                  />
                )}
                <ActionButton
                  fullWidth
                  iconName="fa-pencil"
                  label="重新命名"
                  variant="ghost"
                  onClick={() => {
                    setMenuOpen(false);
                    onRename?.(node);
                  }}
                />
                <ActionButton
                  fullWidth
                  iconName="fa-car-side"
                  label="搬移"
                  variant="ghost"
                  onClick={() => {
                    setMenuOpen(false);
                    onMove?.(node);
                  }}
                />

                <ActionButton
                  fullWidth
                  iconName="fa-trash"
                  label="刪除"
                  variant="ghost"
                  color="warning"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete?.(node);
                  }}
                />
              </div>
            )}
          </div>
        ) : null}
      </div>

      {open && hasKids && (
        <div className="ml-2 pl-2 border-l border-dashed border-gray-300">
          {node.children.map((child) => (
            <TreeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              onAdd={onAdd}
              onRename={onRename}
              onDelete={onDelete}
              editMode={editMode}
              onMove={onMove}
              onChangeCover={onChangeCover}
              isNodeSelectable={isNodeSelectable}
              isLeafSelectable={isLeafSelectable}
            />
          ))}
        </div>
      )}
    </div>
  );
};
